/* eslint-disable react/jsx-no-bind */
import React, { useContext, useState } from 'react';
import toast from 'react-hot-toast';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AuthContext } from 'src/auth/context';
import { UI } from 'src/features/transfer/constants';

function getInitials(user) {
  const name = String(user?.name || '').trim();
  if (name) {
    return name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }
  const phone = String(user?.phoneNumber || '').replace(/\D/g, '');
  if (phone) return phone.slice(-2);
  return '👤';
}

function formatPhoneDisplay(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (/^252\d{9}$/.test(digits)) {
    return `+252 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return `+${digits}`;
}

export default function AccountChip() {
  const { user, logout } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    try {
      setLoggingOut(true);
      await logout();
    } catch (err) {
      console.error('[AccountChip] logout failed', err);
      toast.error('Could not log out. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  const initials = getInitials(user);
  const phoneDisplay = formatPhoneDisplay(user?.phoneNumber);

  let logoutLabel = 'Log out';
  if (loggingOut) {
    logoutLabel = 'Logging out…';
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12.5,
          fontWeight: 700,
          color: UI.green,
          bgcolor: 'rgba(127,212,38,0.15)',
          border: `1px solid rgba(127,212,38,0.3)`,
          userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(127,212,38,0.22)' },
        }}
      >
        {initials}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              bgcolor: UI.panel,
              color: UI.text,
              border: `1px solid ${UI.border}`,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography
            sx={{
              fontSize: 11,
              color: UI.textFaint,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Signed in
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: UI.text, mt: 0.25 }}>
            {phoneDisplay || 'Your account'}
          </Typography>
        </Box>

        <Box sx={{ height: '1px', bgcolor: UI.border, my: 0.5 }} />

        <MenuItem
          onClick={handleLogout}
          disabled={loggingOut}
          sx={{
            fontSize: 14,
            color: UI.danger,
            '&:hover': { bgcolor: 'rgba(242,119,122,0.08)' },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box component="span" sx={{ fontSize: 15 }}>
              ⎋
            </Box>
            <span>{logoutLabel}</span>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
}
